import React, { memo, useCallback, useContext, MouseEvent } from 'react'
import { HorizontalDotsMinor, ReplayMinor } from '@shopify/polaris-icons'
import { MCPayment } from 'src/interfaces/mcPayment'
import { Button } from 'src/components/shared/Button'
import { PaymentStatus } from 'src/constants/payment/enums'
import { colors } from 'src/theme'
import { useDispatch } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { ProcessMCPayment } from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { Tooltip, Typography } from '@mui/material'
import tw from 'twin.macro'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'
import { MCPaymentsTableContext } from '../../payments-table/MCPaymentsTable.context'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'

interface Props {
  payment: MCPayment
}

const statusesWithActions = [
  PaymentStatus.Paid,
  PaymentStatus.NotPaid,
  PaymentStatus.RequiresFunding,
]

const MCPaymentsItemActionButtonComponent = ({ payment }: Props) => {
  const dispatch = useDispatch()

  const { hasPermission } = useCheckPermissions()

  const { reload } = useContext(ReloadWrapperContext)
  const { openMCPaymentsItemAction } = useContext(MCPaymentsTableContext)

  const handleClickAction = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      openMCPaymentsItemAction(event, payment)
    },
    [payment],
  )

  const handleClickRetry = useCallback(async () => {
    try {
      const processedMCPayment: MCPayment = await createAsyncAction(
        dispatch,
        ProcessMCPayment.request({ paymentId: payment.id }),
      )

      reload()

      if (processedMCPayment.status === PaymentStatus.Failed) {
        throw processedMCPayment.failureReason
      }

      if (processedMCPayment.status === PaymentStatus.Pending) {
        showToast('Processing your settlement', { variant: 'warning' })

        return
      }

      showToast('MC Settlement has proceeded', { variant: 'success' })
    } catch {
      showToast(
        <Typography variant="body2">
          This settlement cannot be retried. Please email{' '}
          <a
            href="mailto:support@gigsafe.com"
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              fontWeight: 500,
            }}
          >
            support@gigsafe.com
          </a>{' '}
          for the settlement to be reuploaded and processed for you.
        </Typography>,
        {
          variant: 'error',
          autoHideDuration: 10000,
        },
      )
    }
  }, [payment, reload])

  if (payment.status === PaymentStatus.Failed && hasPermission(UserPermission.PaymentsProcess)) {
    return (
      <Tooltip color="black" title="Retry settlement" placement="left-end">
        <span css={tw`min-w-0 inline-flex`}>
          <Button
            data-testid={`payments-mc-main-row-${payment.id}-retry-btn`}
            color="light"
            size="tiny"
            leftIcon={<ReplayMinor fill={colors.PRIMARY_PURPLE_500} width={20} height={20} />}
            onClick={handleClickRetry}
            disabled={!payment.isRetryable}
          />
        </span>
      </Tooltip>
    )
  }

  return (
    <Button
      data-testid={`payments-mc-main-row-${payment.id}-actions-btn`}
      color="white"
      size="tiny"
      disabled={!statusesWithActions.includes(payment.status)}
      leftIcon={<HorizontalDotsMinor width={20} height={20} />}
      onClick={handleClickAction}
    />
  )
}

export const MCPaymentsItemActionButton = memo(MCPaymentsItemActionButtonComponent)
